<template>
  <!-- Admin "Manage Student Dashboard" row: same filled brand icon as the students' dashboard card. -->
  <div v-if="editable" class="admin-dash-card">
    <md-icon class="admin-dash-card__icon" aria-hidden="true">{{ cardIcon(guide.abbr, guide.icon) }}</md-icon>
    <div class="admin-dash-card__content">
      <div class="admin-dash-card__title">{{ guide.title }}</div>
      <div class="admin-dash-card__desc">{{ guide.description }}</div>
    </div>
    <div class="admin-dash-card__switch" @click.stop>
      <b-form-checkbox name="check-button" switch v-model="guide.enable" @change="() => $emit('enable', guide)" />
    </div>
  </div>
  <div v-else v-click-outside="closeDropdown">
    <router-link :to="editable ? '' : { name: guide.redirectTo }" :style="editable ? 'cursor:grab !important' : ''">
      <div
        @click="() => (guide.redirectTo ? null : onClick())"
        class="card dashboard-card-modern dash-edge"
        :class="guide.redirectTo ? 'mb-3' : 'mb-0'"
      >
        <!-- Secondary-page card: same edge layout as the dashboard cards, icon in place of the photo. -->
        <div class="dash-edge__thumb">
          <md-icon class="dash-edge__icon" aria-hidden="true">{{ guide.icon }}</md-icon>
        </div>
        <div class="dash-edge__content">
          <div class="d-flex align-items-center">
            <div class="dashboard-card__title mb-0">{{ guide.title }}</div>
            <md-icon
              v-if="isActionRequired"
              class="text-warning ml-2"
              style="font-size: 18px"
              v-b-popover.hover.top="'Action Required'"
              aria-label="Action Required"
            >
              info
            </md-icon>
          </div>
          <div class="dashboard-card__desc">{{ guide.description }}</div>
          <span v-if="skillformScore !== null" class="badge badge-light mr-1">
            {{ skillformScoreLabel }}
          </span>
        </div>
        <span class="dash-edge__go" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
            <path d="M9.5 6l6 6-6 6" />
          </svg>
        </span>
      </div>
    </router-link>
    <div v-if="dropdown && !editable" class="card m-0" :class="{ opened: dropdown }">
      <div v-for="item in guide.options" :key="item.id">
        <router-link
          :to="{ name: item.redirectTo }"
          v-if="item.title === 'Universities Pathways' ? get(getLoggedInUser, 'linked_entity.std_portal_uni') : true"
        >
          <div class="card-body py-2">
            <div class="d-flex align-items-center justify-content-between">
              <div class="d-flex align-items-center">
                <md-icon class="text-secondary mr-2">{{ item.icon }}</md-icon>
                <div>
                  <strong>{{ item.title }}</strong>
                </div>
              </div>
            </div>
          </div>
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import { get } from 'lodash';
import { mapGetters } from 'vuex';
import { cardIcon } from '@/common/dashboardCardIcons';

export default {
  props: {
    guide: {
      type: Object,
      default: () => {},
    },
    setupStatuses: {
      type: Object,
      default: () => {},
    },
    editable: {
      type: Boolean,
      default: () => false,
    },
    color: {
      type: String,
      default: () => 'secondary',
    },
  },
  data() {
    return {
      dropdown: false,
    };
  },
  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),
    isActionRequired() {
      return get(this.setupStatuses, this.guide.abbr) === false;
    },
    skillformScore() {
      const profile = get(this.getLoggedInUser, 'student_profile') || {};
      const map = {
        skillform_games: profile.skillform_game_score,
        skillform_exams: profile.skillform_exam_score,
        skillform_flashcards: profile.skillform_flashcard_score,
      };
      const score = map[this.guide.abbr];
      return score !== undefined && score !== null ? score : null;
    },
    skillformScoreLabel() {
      const labels = {
        skillform_games: `Game Points: ${(this.skillformScore || 0).toLocaleString()}`,
        skillform_exams: `Exam Avg: ${this.skillformScore || 0}`,
        skillform_flashcards: `Cards Studied: ${(this.skillformScore || 0).toLocaleString()}`,
      };
      return labels[this.guide.abbr] || '';
    },
  },
  methods: {
    get,
    cardIcon,
    onClick() {
      if (this.guide.isAction) {
        this.$emit('action-click', this.guide);
      } else if (this.guide.handleTranscriptDownload) {
        this.guide.handleTranscriptDownload();
      } else {
        this.dropdown = !this.dropdown;
      }
    },
    closeDropdown() {
      this.dropdown = false;
    },
  },
  directives: {
    'click-outside': {
      bind: function(el, binding, vnode) {
        el.clickOutsideEvent = function(event) {
          // here I check that click was outside the el and his children
          if (!(el == event.target || el.contains(event.target))) {
            // and if it did, call method provided in attribute value
            vnode.context[binding.expression](event);
          }
        };
        document.body.addEventListener('click', el.clickOutsideEvent);
      },
      unbind: function(el) {
        document.body.removeEventListener('click', el.clickOutsideEvent);
      },
    },
  },
};
</script>

<style lang="scss">
.admin-dash-card {
  background: #ffffff;
  border: 1px solid #e8edf4;
  border-radius: 14px;
  overflow: hidden;
  display: flex;
  align-items: stretch;
  gap: 16px;
  padding: 0 18px;
  min-height: 96px;
  margin-bottom: 1rem;
  cursor: grab;
  box-shadow: 0 2px 6px rgba(24, 39, 75, 0.05);

  &__icon {
    align-self: center;
    color: var(--card-brand, var(--primary-color, #3b86f5)) !important;
    font-size: 32px !important;
    width: 32px;
    min-width: 32px;
    height: 32px;
  }

  &__content {
    flex: 1;
    min-width: 0;
    align-self: center;
    padding: 14px 0;
  }

  &__title {
    font-weight: 700;
    font-size: 16px;
    color: #181818;
    line-height: 1.3;
  }

  &__desc {
    margin-top: 2px;
    color: #58615c;
    font-size: 13px;
  }

  &__switch {
    align-self: center;
    flex: none;
    cursor: default;
  }
}

// Edge layout (matches the dashboard CategoryCard): full-height grey panel on the left with the
// icon in the brand color, text, then a round arrow in the brand color.
.dash-edge {
  --brand: var(--primary-color, #3b86f5);
  flex-direction: row;
  align-items: stretch;
  gap: 14px;
  padding-right: 14px;
  min-height: 96px;
  height: auto !important;
  overflow: hidden;
  border: 0 !important;
  box-shadow: 0 1px 2px rgba(20, 28, 40, 0.06), 0 8px 24px -12px rgba(20, 28, 40, 0.18) !important;

  .dash-edge__thumb {
    width: 96px;
    flex: none;
    background: #ececec;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .dash-edge__icon {
    color: var(--brand) !important;
    font-size: 34px !important;
    width: 34px;
    min-width: 34px;
    height: 34px;
  }

  .dash-edge__content {
    flex: 1;
    min-width: 0;
    align-self: center;
    padding: 14px 0;
  }

  .dashboard-card__title {
    color: #181818;
  }

  .dashboard-card__desc {
    color: #58615c;
  }

  .dash-edge__go {
    align-self: center;
    flex: none;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--brand);
    background: #f1f1f1;
    background: color-mix(in srgb, var(--brand) 12%, #ffffff);
    transition: background 0.2s ease, color 0.2s ease;
  }

  &:hover .dash-edge__go {
    background: var(--brand);
    color: #ffffff;
  }
}

.dashboard-card-modern {
  border: 1px solid #e8edf4;
  border-radius: 14px;
  height: 85%;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
  box-shadow: 0 2px 6px rgba(24, 39, 75, 0.05);

  &:hover {
    box-shadow: 0 8px 20px rgba(24, 39, 75, 0.12);
    transform: translateY(-2px);
  }
}

.dashboard-card__icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: #e8f3fb;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  .setup-guide-icon {
    font-size: 24px;
    width: 24px;
    height: 24px;
    line-height: 1;
    color: #3a7af3;
  }
}

.dashboard-card__title {
  font-weight: 700;
  font-size: 16px;
  color: #12263f;
  line-height: 1.3;
}

.dashboard-card__desc {
  margin-top: 2px;
  color: #6b7b8f;
  font-size: 13px;
}

.dashboard-card__cta {
  border-radius: 22px !important;
  min-width: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 10px;
}

.opened {
  position: absolute;
  z-index: 1;
  width: 95%;
  top: 100px;
  box-shadow: 0px 0px 2px 2px lightgrey !important;
  animation: fadeIn 400ms;
  transform: translate(1px, 0px);
}

@keyframes fadeIn {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}
</style>
